import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api.js';
import { formatEur } from '@csoki/shared';
import type { CatalogEntry, CatalogOffer } from '@csoki/shared';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner.js';

export function Catalog({ kioskMode = false }: { kioskMode?: boolean } = {}) {
  const [entries, setEntries] = useState<CatalogEntry[] | null>(null);
  const [scanning, setScanning] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const nav = useNavigate();

  useEffect(() => {
    load();
  }, []);
  async function load() {
    try {
      const d = await api<{ entries: CatalogEntry[] }>('GET', '/api/catalog');
      setEntries(d.entries);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  const onDetected = useCallback(
    (code: string) => {
      setScanning(false);
      const match = entries?.find((e) => e.product.barcode === code.trim());
      if (match) {
        document.getElementById(`prod-${match.product.barcode}`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      } else {
        alert('No listings for that barcode yet.');
      }
    },
    [entries],
  );
  const scanner = useBarcodeScanner({ active: scanning, onDetected });

  if (!entries) return <div className="page">Loading…</div>;

  return (
    <div className="page catalog">
      <header className="catalog-header">
        <h1>Csokibolt</h1>
        <p className="tagline">
          Scan, pick, pay at the kiosk — chocolate on an honesty box.
        </p>
        <div className="actions">
          <button onClick={() => setScanning(true)}>Scan</button>
          {!kioskMode && (
            <Link to="/seller/login" className="seller-link">
              Seller
            </Link>
          )}
        </div>
        <Sparkle className="sparkle sparkle-1" />
        <Sparkle className="sparkle sparkle-2" />
      </header>

      {scanning && (
        <div className="scanner">
          <video ref={scanner.videoRef} playsInline muted />
          <button onClick={() => setScanning(false)}>Cancel</button>
        </div>
      )}

      {err && <div className="err">{err}</div>}
      {entries.length === 0 && <p className="hint">Nothing in stock right now.</p>}

      <ul className="products stagger">
        {entries.map((e) => (
          <ProductRow
            key={e.product.barcode}
            entry={e}
            kioskMode={kioskMode}
            onBought={(purchaseId) => {
              if (!kioskMode) nav(`/pending/${purchaseId}`);
            }}
          />
        ))}
      </ul>
    </div>
  );
}

function Sparkle({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z"
        fill="#FFF2A8"
        stroke="#0A0A0A"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ProductRow({
  entry,
  onBought,
  kioskMode = false,
}: {
  entry: CatalogEntry;
  onBought: (purchaseId: string) => void;
  kioskMode?: boolean;
}) {
  const [selected, setSelected] = useState<CatalogOffer>(entry.offers[0]!);
  const [qtyStr, setQtyStr] = useState('1');
  const [busy, setBusy] = useState(false);
  const maxQty = selected.stock;
  const parsedQty = Math.floor(Number(qtyStr));
  const qty =
    Number.isFinite(parsedQty) && parsedQty >= 1
      ? Math.min(maxQty, parsedQty)
      : 1;
  const totalCents = selected.priceCents * qty;
  const belowMin = totalCents < MIN_PURCHASE_CENTS;

  async function buy() {
    if (belowMin) return;
    setBusy(true);
    try {
      const d = await api<{ purchaseId: string }>('POST', '/api/purchases', {
        listingId: selected.listingId,
        quantity: qty,
      });
      onBought(d.purchaseId);
      // On the kiosk, leave the button in its busy state — the WebSocket will
      // swap this whole view for KioskPayment within a tick.
      if (kioskMode) return;
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === 'kiosk_busy')
          alert('Someone is paying at the kiosk. Try again in a moment.');
        else if (e.code === 'insufficient_stock') alert('Just sold out.');
        else alert(e.message || 'Could not start purchase.');
      }
      setBusy(false);
      return;
    }
    setBusy(false);
  }

  return (
    <li className="product" id={`prod-${entry.product.barcode}`}>
      <div className="thumb-wrap">
        {entry.product.imageUrl ? (
          <img src={entry.product.imageUrl} alt="" />
        ) : (
          <svg viewBox="0 0 40 40" width="40" height="40" aria-hidden="true">
            <rect
              x="6" y="10" width="28" height="22" rx="3"
              fill="#FFDAB4" stroke="#0A0A0A" strokeWidth="1.5"
            />
            <path d="M10 10 L10 32 M16 10 L16 32 M22 10 L22 32 M28 10 L28 32"
              stroke="#0A0A0A" strokeWidth="1" />
          </svg>
        )}
      </div>
      <div className="main">
        <div className="name">{entry.product.name}</div>
        {entry.product.brand && <div className="brand">{entry.product.brand}</div>}
        <div className="offers">
          {entry.offers.map((o) => (
            <label key={o.listingId} className={o.listingId === selected.listingId ? 'sel' : ''}>
              <input
                type="radio"
                name={`offer-${entry.product.barcode}`}
                checked={o.listingId === selected.listingId}
                onChange={() => {
                  setSelected(o);
                  setQtyStr(String(Math.min(qty, o.stock)));
                }}
              />
              <span className="seller">{o.sellerDisplayName}</span>
              <span className="price">{formatEur(o.priceCents)}</span>
              <span className="stock">{o.stock} left</span>
            </label>
          ))}
        </div>
        <div className="buy-row">
          <input
            type="number"
            min={1}
            max={maxQty}
            value={qtyStr}
            onChange={(e) => setQtyStr(e.target.value)}
            onBlur={() => setQtyStr(String(qty))}
          />
          <button onClick={buy} disabled={busy || belowMin}>
            {busy ? '…' : `Buy — ${formatEur(totalCents)}`}
          </button>
        </div>
        {belowMin && (
          <div className="min-hint">
            Minimum purchase is {formatEur(MIN_PURCHASE_CENTS)}. Bump the quantity to continue.
          </div>
        )}
      </div>
    </li>
  );
}

const MIN_PURCHASE_CENTS = 100;
